/**
 * Confirmation Handler - Generates human-readable confirmations before executing commands
 */

import { ParsedCommand, ConfirmationData } from './types';

export function generateConfirmation(command: ParsedCommand, previewData?: any): ConfirmationData {
  const { intent, entities } = command;
  
  let preview = '';
  const warnings: string[] = [];
  let affectedRecords;

  switch (intent) {
    case 'query_service_requests':
      preview = generateQueryPreview('service requests', entities.filters);
      break;

    case 'update_service_request':
      preview = generateUpdatePreview('service request', entities.target_id, entities.updates, previewData);
      if (entities.updates?.status === 'completed') {
        warnings.push('This will automatically set the completed_at timestamp');
      }
      break;

    case 'create_service_request':
      preview = generateCreatePreview('service request', entities.new_record);
      break;

    case 'query_admin_tasks':
      preview = generateQueryPreview('admin tasks', entities.filters);
      break;

    case 'create_admin_task':
      preview = generateCreatePreview('admin task', entities.new_record);
      break;

    case 'update_admin_task':
      preview = generateUpdatePreview('admin task', entities.target_id, entities.updates, previewData);
      if (entities.updates?.status === 'completed') {
        warnings.push('This will set the completed_at timestamp');
      }
      break;

    case 'lookup_user':
      preview = generateLookupPreview('user', entities.target_user || entities.target_email);
      break;

    case 'query_users':
      preview = generateQueryPreview('users', entities.filters);
      break;

    case 'update_user':
      preview = generateUpdatePreview('user', entities.target_id || entities.target_user, entities.updates, previewData);
      if (entities.updates?.lifecycle_status) {
        warnings.push('This will update the lifecycle_updated_at timestamp');
      }
      if (entities.updates?.home_manager) {
        warnings.push('This will update the home_manager_assigned_at timestamp');
      }
      break;

    case 'query_inventory':
      preview = generateQueryPreview('inventory items', entities.filters);
      break;

    case 'create_inventory_item':
      preview = generateCreatePreview('inventory item', entities.new_record);
      break;

    case 'update_inventory_item':
      preview = generateUpdatePreview('inventory item', entities.target_id, entities.updates, previewData);
      break;

    case 'help':
      preview = 'Display help information about available commands';
      break;

    case 'clarification_needed':
      preview = command.clarificationNeeded || 'Need more information to proceed';
      break;

    default:
      preview = `Execute operation: ${intent}`;
  }

  if (previewData?.count !== undefined) {
    affectedRecords = {
      type: getRecordType(intent),
      count: previewData.count,
      examples: previewData.examples,
    };
  }

  return {
    command,
    preview,
    warnings: warnings.length > 0 ? warnings : undefined,
    affectedRecords,
  };
}

function generateQueryPreview(recordType: string, filters?: Record<string, any>): string {
  if (!filters || Object.keys(filters).length === 0) {
    return `Query all ${recordType}`;
  }

  const filterParts: string[] = [];
  
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        filterParts.push(`${key} in [${value.join(', ')}]`);
      } else {
        filterParts.push(`${key} = ${value}`);
      }
    }
  }

  return `Query ${recordType} where ${filterParts.join(' AND ')}`;
}

function generateUpdatePreview(
  recordType: string,
  targetId?: string,
  updates?: Record<string, any>,
  previewData?: any
): string {
  const recordName = previewData?.title || previewData?.full_name || previewData?.name || targetId || 'record';
  
  if (!updates || Object.keys(updates).length === 0) {
    return `Update ${recordType} "${recordName}"`;
  }

  const updateParts: string[] = [];
  
  for (const [key, value] of Object.entries(updates)) {
    if (value !== undefined && value !== null) {
      updateParts.push(`${key} → ${value}`);
    }
  }

  return `Update ${recordType} "${recordName}": ${updateParts.join(', ')}`;
}

function generateCreatePreview(recordType: string, newRecord?: Record<string, any>): string {
  if (!newRecord) {
    return `Create new ${recordType}`;
  }

  const title = newRecord.title || newRecord.name || 'new record';
  return `Create ${recordType}: "${title}"`;
}

function generateLookupPreview(recordType: string, identifier?: string): string {
  if (!identifier) {
    return `Look up ${recordType}`;
  }
  return `Look up ${recordType}: ${identifier}`;
}

function getRecordType(intent: string): string {
  if (intent.includes('service_request')) return 'service_requests';
  if (intent.includes('admin_task')) return 'admin_tasks';
  if (intent.includes('user')) return 'users';
  if (intent.includes('inventory')) return 'inventory_items';
  return 'records';
}

export function formatExecutionResult(result: any, intent: string): string {
  if (intent.startsWith('query_')) {
    const count = Array.isArray(result) ? result.length : 0;
    return `Found ${count} result${count !== 1 ? 's' : ''}`;
  }

  if (intent.startsWith('create_')) {
    return `Successfully created new record`;
  }

  if (intent.startsWith('update_')) {
    return `Successfully updated record`;
  }

  return 'Operation completed successfully';
}
